"use client";

import { useCallback, useState } from "react";
import { safeLocalStorage } from "@/lib/storage/safe-storage";
import type { RecordColumnState } from "../types";
import { useHasMounted } from "@/hooks/use-has-mounted";
import { isUnknownRecord } from "@shared/validation/narrow";

interface UseColumnStateOptions {
  objectId: string;
  defaultColumns: string[];
  allColumns: string[];
  urlOrder: string[] | undefined;
  urlHidden: string[] | undefined;
  onUrlChange: (
    order: string[] | undefined,
    hidden: string[] | undefined,
  ) => void;
}

interface StoredColumnState extends Partial<RecordColumnState> {
  layoutVersion?: number;
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === "string");
}

function readWidths(value: unknown): Record<string, number> | undefined {
  if (!isUnknownRecord(value)) return undefined;
  const widths: Record<string, number> = {};
  for (const [columnId, width] of Object.entries(value)) {
    if (typeof width === "number") widths[columnId] = width;
  }
  return widths;
}

function parseStoredColumnState(raw: string): StoredColumnState {
  const parsed: unknown = JSON.parse(raw);
  if (!isUnknownRecord(parsed)) return {};
  return {
    layoutVersion:
      typeof parsed.layoutVersion === "number" ? parsed.layoutVersion : undefined,
    order: isStringArray(parsed.order) ? parsed.order : undefined,
    hidden: isStringArray(parsed.hidden) ? parsed.hidden : undefined,
    widths: readWidths(parsed.widths),
  };
}

const OBJECT_LAYOUT_VERSIONS: Record<string, number> = {
  accounts: 8,
  leads: 6,
  outreach: 5,
  orders: 9,
  quotes: 5,
  users: 3,
};

const BASE_LAYOUT_VERSION = 3;

export function layoutVersionFor(objectId: string): number {
  return OBJECT_LAYOUT_VERSIONS[objectId] ?? BASE_LAYOUT_VERSION;
}

function storageKey(objectId: string): string {
  return `record-columns:${objectId}`;
}

function writeStored(objectId: string, columns: RecordColumnState): void {
  safeLocalStorage().setItem(
    storageKey(objectId),
    JSON.stringify({
      ...columns,
      layoutVersion: layoutVersionFor(objectId),
    }),
  );
}

function defaultState(
  defaultColumns: string[],
  allColumns: string[],
): RecordColumnState {
  return {
    order: defaultColumns,
    hidden: allColumns.filter((id) => !defaultColumns.includes(id)),
    widths: {},
  };
}

function haveSameWidths(
  current: Record<string, number>,
  next: Record<string, number>,
): boolean {
  const currentEntries = Object.entries(current);
  return (
    currentEntries.length === Object.keys(next).length &&
    currentEntries.every(([columnId, width]) => next[columnId] === width)
  );
}

function readStored(objectId: string): RecordColumnState | null {
  const raw = safeLocalStorage().getItem(storageKey(objectId));
  if (!raw) return null;
  try {
    const parsed = parseStoredColumnState(raw);
    const storedVersion = parsed.layoutVersion ?? BASE_LAYOUT_VERSION;
    if (storedVersion !== layoutVersionFor(objectId)) {
      safeLocalStorage().removeItem(storageKey(objectId));
      return null;
    }
    if (!parsed.order || !parsed.hidden) {
      return null;
    }
    if (parsed.layoutVersion !== undefined) {
      return {
        order: parsed.order,
        hidden: parsed.hidden,
        widths: parsed.widths ?? {},
      };
    }

    const migrated: RecordColumnState = {
      order: parsed.order,
      hidden: parsed.hidden,
      widths: {},
    };
    writeStored(objectId, migrated);
    return migrated;
  } catch {
    return null;
  }
}

function withUrlColumns(
  base: RecordColumnState,
  urlOrder: UseColumnStateOptions["urlOrder"],
  urlHidden: UseColumnStateOptions["urlHidden"],
): RecordColumnState {
  return {
    order: urlOrder ?? base.order,
    hidden: urlHidden ?? base.hidden,
    widths: base.widths,
  };
}

export function useColumnState({
  objectId,
  defaultColumns,
  allColumns,
  urlOrder,
  urlHidden,
  onUrlChange,
}: UseColumnStateOptions) {
  const fallback = defaultState(defaultColumns, allColumns);
  const [stored, setStored] = useState<RecordColumnState | null>(null);
  const [storedFor, setStoredFor] = useState<string | null>(null);
  const isHydrated = useHasMounted();

  if (isHydrated && storedFor !== objectId) {
    setStoredFor(objectId);
    setStored(readStored(objectId));
  }

  const base = stored ?? fallback;
  const isUrlDriven = urlOrder !== undefined || urlHidden !== undefined;
  const columns: RecordColumnState = isUrlDriven
    ? withUrlColumns(base, urlOrder, urlHidden)
    : base;
  const setColumns = useCallback(
    (next: RecordColumnState) => {
      if (isUrlDriven) {
        onUrlChange(next.order, next.hidden);
        const current = stored ?? fallback;
        if (!haveSameWidths(current.widths, next.widths)) {
          const persisted = { ...current, widths: next.widths };
          setStored(persisted);
          writeStored(objectId, persisted);
        }
        return;
      }
      setStored(next);
      writeStored(objectId, next);
    },
    [fallback, isUrlDriven, objectId, onUrlChange, stored],
  );

  const resetColumns = useCallback(() => {
    safeLocalStorage().removeItem(storageKey(objectId));
    setStored(null);
    if (isUrlDriven) onUrlChange(undefined, undefined);
  }, [objectId, isUrlDriven, onUrlChange]);

  return { columns, setColumns, resetColumns };
}
